import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Divider } from "@material-ui/core";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { Column } from "devextreme-react/data-grid";
import { TextBox, Button as TextBoxButton } from "devextreme-react/text-box";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { openModal } from "../../redux/modal/modalSlice";
import DateBox from "../common/CustomDateBox";
import Table from "../table/Table";
import CustomPagination from "../table/CustomPagination";
import { baseRequest } from "../../fetch/basicThunk";
import { QUERY_GET_BILLING_SUPPLIER_TICKETS, QUERY_POST_BILLING_EXPORT } from "../../fetch/constantQueries";
import { _base64ToArrayBuffer, getCaseStateIcon } from "../../commonFunctions/commonFunctions";
import searchIcon from "../../images/ui-ic-search.svg";
import exportIcon from "../../images/export.svg";
import alertInfoBlue from "../../images/alert-info-blue.svg";
import alertWarningFilled from "../../images/alert-warning-filled.svg";
import alertSuccessFilled from "../../images/alert-success-filled.svg";
import alertErrorFilled from "../../images/alert-error-filled.svg";

const useStyles = makeStyles({
  bigNumbersWrapper: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "0.75rem",
    marginBottom: "1.25rem",
    "@media (max-width: 960px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  bigNumberCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 0,
    padding: 0,
    border: "1px solid #e0e0e0",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  bigNumberHeader: {
    padding: "10px 16px 8px 16px",
    display: "flex",
    alignItems: "center",
  },
  bigNumberDivider: {
    backgroundColor: "#e0e0e0",
    width: "100%",
  },
  bigNumberBody: {
    padding: "10px 16px 12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    minWidth: 0,
  },
  bigNumberIcon: {
    width: "28px",
    height: "28px",
    flexShrink: 0,
  },
  bigNumberValuesWrapper: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    justifyContent: "center",
  },
  bigNumberTitle: {
    fontSize: "0.7rem",
    color: "#555555",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontWeight: 600,
  },
  bigNumberValue: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#000000",
    marginBottom: "2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  bigNumberSubValue: {
    fontSize: "0.8rem",
    color: "#555555",
    fontWeight: 500,
  },
});

export function SupplierPaymentOrders() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [appliedFilterText, setAppliedFilterText] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterKey, setFilterKey] = useState(0);
  const [summary, setSummary] = useState({
    totalCount: 0,
    totalValue: 0,
    pendingCount: 0,
    pendingValue: 0,
    approvedCount: 0,
    approvedValue: 0,
    rejectedCount: 0,
    rejectedValue: 0,
  });

  const fetchTickets = useCallback(() => {
    setLoading(true);
    const queryParts = [
      `pageIndex=${currentPage}`,
      `pageNum=${pageSize}`,
    ];
    if (appliedFilterText && appliedFilterText.trim()) {
      queryParts.push(`searchString=${encodeURIComponent(appliedFilterText.trim())}`);
    }
    if (appliedStartDate) {
      queryParts.push(`creationDate=${dayjs(appliedStartDate).format("YYYY-MM-DD")}`);
    }
    if (appliedEndDate) {
      queryParts.push(`creationDateUntil=${dayjs(appliedEndDate).format("YYYY-MM-DD")}`);
    }

    const query = `?${queryParts.join("&")}`;

    baseRequest({ query }, dispatch, QUERY_GET_BILLING_SUPPLIER_TICKETS, "GET")
      .then((data) => {
        if (data && Array.isArray(data.elements)) {
          setTickets(data.elements);
          setTotalRecords(data.totalRecords || 0);
        } else if (data && Array.isArray(data.items)) {
          setTickets(data.items);
          setTotalRecords(data.totalItems || 0);
        } else if (Array.isArray(data)) {
          setTickets(data);
          setTotalRecords(data.length);
        } else {
          setTickets([]);
          setTotalRecords(0);
        }

        if (data && data.summary) {
          setSummary(data.summary);
        } else {
          setSummary({
            totalCount: 0,
            totalValue: 0,
            pendingCount: 0,
            pendingValue: 0,
            approvedCount: 0,
            approvedValue: 0,
            rejectedCount: 0,
            rejectedValue: 0,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setTickets([]);
        setTotalRecords(0);
        setLoading(false);
      });
  }, [currentPage, pageSize, appliedFilterText, appliedStartDate, appliedEndDate, dispatch]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleApplyFilters = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCurrentPage(1);
    setAppliedFilterText(filterText);
    setAppliedStartDate(filterStartDate);
    setAppliedEndDate(filterEndDate);
  };

  const handleClearFilters = () => {
    setFilterText("");
    setAppliedFilterText("");
    setFilterStartDate(null);
    setAppliedStartDate(null);
    setFilterEndDate(null);
    setAppliedEndDate(null);
    setCurrentPage(1);
    setFilterKey((prev) => prev + 1);
  };

  const showClearFilters = () => {
    return Boolean(appliedFilterText || appliedStartDate || appliedEndDate || filterText || filterStartDate || filterEndDate);
  };

  const handleExportExcel = async () => {
    const bodyData = {
      SearchString: appliedFilterText || filterText || null,
      CreationDate: appliedStartDate || filterStartDate || null,
      CreationDateUntil: appliedEndDate || filterEndDate || null,
    };

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
      const queryParams = `?tab=supplier&timeZone=${encodeURIComponent(timezone)}`;
      const data = await baseRequest(
        { body: bodyData, query: queryParams },
        dispatch,
        QUERY_POST_BILLING_EXPORT,
        "POST"
      );

      if (data) {
        const dateStr = "_" + dayjs().format("YYYYMMDD") + "_" + dayjs().format("HHmmss");
        const filename = "Ordens_de_Pagamento" + dateStr + ".xlsx";
        saveAs(new Blob([_base64ToArrayBuffer(data)], { type: "application/octet-stream" }), filename);
      } else {
        dispatch(
          openModal({
            type: "warning",
            message: t("SupplierPaymentOrders.NoDataToExport"),
          })
        );
      }
    } catch (err) {
      dispatch(
        openModal({
          type: "warning",
          message: err?.message || t("SupplierPaymentOrders.ErrorExport"),
        })
      );
    }
  };

  const renderCaseStateColumn = (stateId, stateName) => {
    if (stateId === null || stateId === undefined) {
      return (
        <div className="active-column-cell">
          <span>{stateName || ""}</span>
        </div>
      );
    }
    const colorCode = getCaseStateIcon(stateId);
    return (
      <div className="active-column-cell">
        <img width={20} src={colorCode} title={stateName || ""} alt="" />
        <span>{stateName || ""}</span>
      </div>
    );
  };

  const bigNumbers = useMemo(() => [
    {
      key: "total",
      title: t("SupplierPaymentOrders.SummaryTotalTitle"),
      value: `R$ ${Number(summary.totalValue || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      countText: `${summary.totalCount || 0} ${summary.totalCount === 1 ? t("SupplierPaymentOrders.TicketCountSingular") : t("SupplierPaymentOrders.TicketCountPlural")}`,
      icon: alertInfoBlue,
    },
    {
      key: "pending",
      title: t("SupplierPaymentOrders.SummaryPendingTitle"),
      value: `R$ ${Number(summary.pendingValue || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      countText: `${summary.pendingCount || 0} ${summary.pendingCount === 1 ? t("SupplierPaymentOrders.TicketCountSingular") : t("SupplierPaymentOrders.TicketCountPlural")}`,
      icon: alertWarningFilled,
    },
    {
      key: "approved",
      title: t("SupplierPaymentOrders.SummaryApprovedTitle"),
      value: `R$ ${Number(summary.approvedValue || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      countText: `${summary.approvedCount || 0} ${summary.approvedCount === 1 ? t("SupplierPaymentOrders.TicketCountSingular") : t("SupplierPaymentOrders.TicketCountPlural")}`,
      icon: alertSuccessFilled,
    },
    {
      key: "rejected",
      title: t("SupplierPaymentOrders.SummaryRejectedTitle"),
      value: `R$ ${Number(summary.rejectedValue || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      countText: `${summary.rejectedCount || 0} ${summary.rejectedCount === 1 ? t("SupplierPaymentOrders.TicketCountSingular") : t("SupplierPaymentOrders.TicketCountPlural")}`,
      icon: alertErrorFilled,
    },
  ], [summary, t]);

  const columns = useMemo(() => [
    <Column
      key="col-case-id"
      dataField="caseId"
      caption={t("CaseId")}
      alignment="right"
    />,
    <Column
      key="col-order-number"
      dataField="orderNumber"
      caption={t("SupplierPaymentOrders.ColOrderNumber")}
      cellRender={(row) => row.data?.orderNumber || "-"}
    />,
    <Column
      key="col-plate"
      width="8%"
      dataField="vehiclePlate"
      caption={t("Plate")}
      cellRender={(row) => row.data?.vehiclePlate || "-"}
    />,
    <Column
      key="col-total-value"
      width="10%"
      dataField="totalValue"
      caption={t("BillingContent.TotalValue")}
      alignment="right"
      cellRender={(row) => {
        const val = row.data?.totalValue;
        if (val == null) return "-";
        return `R$ ${Number(val).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }}
    />,
    <Column
      key="col-qty"
      width="8%"
      dataField="paymentOrdersCount"
      caption={t("BillingContent.PaymentOrdersCount")}
      alignment="right"
      cellRender={(row) => `${row.data?.paymentOrdersCount || 0} OP${row.data?.paymentOrdersCount === 1 ? "" : "s"}`}
    />,
    <Column
      key="col-start-date"
      width="12%"
      dataField="startDate"
      caption={t("BillingContent.FilterCaseStartDate")}
      dataType="date"
      format="dd/MM/yyyy HH:mm"
    />,
    <Column
      key="col-end-date"
      width="12%"
      dataField="endDate"
      caption={t("BillingContent.FilterCaseEndDate")}
      dataType="date"
      format="dd/MM/yyyy HH:mm"
    />,
    <Column
      key="col-state"
      dataField="caseState"
      caption={t("State")}
      cellRender={(data) => renderCaseStateColumn(data?.data?.caseStateId, data?.value)}
    />,
    <Column
      key="col-actions"
      width="6%"
      caption={t("Actions")}
      alignment="center"
      cellRender={() => null}
    />,
  ], [t]);

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  return (
    <div style={{ margin: "1rem" }}>
      <form
        key={filterKey}
        onSubmit={handleApplyFilters}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "1.25rem",
        }}
      >
        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <TextBox
            label={`${t("Search")}...`}
            value={filterText}
            mode="text"
            onEnterKey={handleApplyFilters}
            onValueChange={(val) => setFilterText(val)}
            valueChangeEvent="keyup"
          >
            <TextBoxButton
              name="search"
              location="after"
              options={{
                icon: searchIcon,
                stylingMode: "text",
                onClick: handleApplyFilters,
              }}
            />
          </TextBox>
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <DateBox
            label={t("BillingContent.FilterCaseStartDate")}
            labelMode="floating"
            placeholder={t("BillingContent.FilterCaseStartDate")}
            useMaskBehavior={true}
            dateSerializationFormat="yyyy-MM-dd"
            displayFormat="dd/MM/yyyy"
            value={filterStartDate}
            onValueChanged={(e) => setFilterStartDate(e.value)}
            type="date"
            acceptCustomValue={false}
            pickerType="calendar"
            buttons={["dropDown"]}
          />
        </div>

        <div style={{ minWidth: "200px", maxWidth: "300px", flex: "1 1 240px" }}>
          <DateBox
            label={t("BillingContent.FilterCaseEndDate")}
            labelMode="floating"
            placeholder={t("BillingContent.FilterCaseEndDate")}
            useMaskBehavior={true}
            dateSerializationFormat="yyyy-MM-dd"
            displayFormat="dd/MM/yyyy"
            value={filterEndDate}
            onValueChanged={(e) => setFilterEndDate(e.value)}
            type="date"
            acceptCustomValue={false}
            pickerType="calendar"
            buttons={["dropDown"]}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleApplyFilters}
            style={{ borderRadius: 0 }}
          >
            {t("BillingContent.FilterButtonApply")}
          </Button>
          {showClearFilters() && (
            <div style={{ cursor: "pointer" }}>
              <DeleteOutlinedIcon
                style={{ verticalAlign: "middle", color: "#015BA8" }}
                onClick={handleClearFilters}
              />
            </div>
          )}
        </div>

        <div style={{ flexGrow: 1 }} />

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Button
            startIcon={
              <img
                style={{
                  height: "1.25rem",
                  width: "1.25rem",
                  filter: "brightness(0) invert(1)",
                }}
                src={exportIcon}
                alt=""
              />
            }
            variant="contained"
            color="primary"
            onClick={handleExportExcel}
            style={{ borderRadius: 0 }}
          >
            {t("Export")}
          </Button>
        </div>
      </form>

      <div className={classes.bigNumbersWrapper}>
        {bigNumbers.map((item) => (
          <div
            key={item.key}
            className={classes.bigNumberCard}
          >
            <div className={classes.bigNumberHeader}>
              <span className={classes.bigNumberTitle}>
                {item.title}
              </span>
            </div>
            <Divider className={classes.bigNumberDivider} />
            <div className={classes.bigNumberBody}>
              {item.icon && (
                <img
                  src={item.icon}
                  alt=""
                  className={classes.bigNumberIcon}
                />
              )}
              <div className={classes.bigNumberValuesWrapper}>
                <span className={classes.bigNumberValue}>
                  {item.value}
                </span>
                <span className={classes.bigNumberSubValue}>
                  {item.countText}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Divider style={{ marginBottom: "1.5rem" }} />

      <Table
        datasource={tickets}
        loading={loading ? "loading" : false}
        columnasTabla={columns}
      />

      <CustomPagination
        pageSize={pageSize}
        pageIndex={currentPage}
        count={totalRecords}
        totalPages={totalPages}
        handlePageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        handlePageIndexChange={(e, p) => setCurrentPage(p)}
        loading={loading}
      />
    </div>
  );
}

export default SupplierPaymentOrders;
